import {objectFromEntries, selectDefined} from '@benchling/js-utils';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {getViewForViewReference} from 'analysis-tool/data-view/AnalysisViewUtils';
import {ColumnTypeValidationFlag, ColumnTypeValidationOptions} from 'data-frames/util/validateColumnType';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FIELD_ORDER_UI_SCHEMA_KEY,
  FieldSettingsV2,
  ShapeUISchemaV2,
  StepConfigEditorOverride,
} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {omit} from 'util/omitKey';
import {assertNever} from 'util/Types';

import {
  AnalysisConfigForm,
  AnalysisConfigFormField,
  AnalysisGrid,
} from './analysis-adapter/AnalysisAdapterTypes';
import {resolveConfigReference} from './analysis-adapter/AnalysisAdapterUtils';
import {DATA_LAYOUT_INPUT_DATA_NAME} from './DataLayoutConstants';
import {Grid, DataLayout, ConfigFormBlock, ConfigFormField} from './DataLayoutTypes';

function convertColumnTypeValidationOptions(
  columnTypeValidationOptions: Extract<
    AnalysisConfigFormField['configSpec'],
    {kind: 'columnReference'}
  >['validationOptions']
): ColumnTypeValidationOptions | null {
  if (columnTypeValidationOptions == null) {
    return null;
  }

  return selectDefined({
    ...columnTypeValidationOptions,
    allowedLeafTypes:
      columnTypeValidationOptions.allowedLeafTypes == null
        ? undefined
        : columnTypeValidationOptions.allowedLeafTypes.map((leafType) => LeafType[leafType]),
    listType:
      columnTypeValidationOptions.listType == null
        ? undefined
        : ColumnTypeValidationFlag[columnTypeValidationOptions.listType],
    nullableType:
      columnTypeValidationOptions.nullableType == null
        ? undefined
        : ColumnTypeValidationFlag[columnTypeValidationOptions.nullableType],
    maybeTextType:
      columnTypeValidationOptions.maybeTextType == null
        ? undefined
        : ColumnTypeValidationFlag[columnTypeValidationOptions.maybeTextType],
    strictLeafType: columnTypeValidationOptions.strictLeafType ?? undefined,
  });
}

function convertAnalysisConfigSpec(
  configSpec: AnalysisConfigFormField['configSpec'],
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: AnalysisGraphQL
): Extract<PipelineConfigJsonSpec, {kind: 'shape'}> {
  switch (configSpec.kind) {
    case 'columnReference':
      return {
        kind: 'shape',
        required: true,
        fields: {
          field: {
            ...configSpec,
            inputDataName: DATA_LAYOUT_INPUT_DATA_NAME,
            validationOptions: convertColumnTypeValidationOptions(configSpec.validationOptions),
          },
        },
      };
    case 'columnValue':
      return {
        kind: 'shape',
        required: true,
        fields: {
          field: configSpec,
        },
      };
    case 'integer':
      return {
        kind: 'shape',
        required: true,
        fields: {
          field: selectDefined({
            ...configSpec,
            minValue: configSpec.minValue ?? undefined,
            maxValue: configSpec.maxValue ?? undefined,
          }),
        },
      };
    case 'string':
      return {
        kind: 'shape',
        required: true,
        fields: {
          field: selectDefined({
            ...configSpec,
            value: configSpec.value ?? undefined,
          }),
        },
      };
    case 'boolean':
    case 'enum':
    case 'number':
    case 'tableReference':
      return {
        kind: 'shape',
        required: true,
        fields: {
          field: configSpec,
        },
      };
    default:
      throw new Error(`Found unexpected config spec kind ${assertNever(configSpec).kind} in layout`);
  }
}

function convertFieldSettings(
  fieldSettings: AnalysisConfigFormField['uiSchema']['fieldSettings']
): FieldSettingsV2 | null {
  if (fieldSettings == null) {
    return null;
  }

  const fieldType = fieldSettings.type;
  const baseFieldSettings = omit(fieldSettings, 'type');

  switch (fieldType) {
    case 'CHECKBOX_SETTINGS':
      return {
        ...baseFieldSettings,
        checkboxLabel: fieldSettings.checkboxLabel,
      };
    case 'COLUMN_REFERENCE_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        noColumnSelectedText: fieldSettings.noColumnSelectedText ?? undefined,
        allowSelectNull: fieldSettings.allowSelectNull ?? undefined,
      });
    case 'COLUMN_VALUE_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        noValueSelectedText: fieldSettings.noValueSelectedText ?? undefined,
      });
    case 'ENUM_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        displayNames:
          fieldSettings.displayNames == null
            ? undefined
            : objectFromEntries(
                fieldSettings.displayNames.map(({value, displayName}) => [value, displayName])
              ),
      });
    case 'TABLE_REFERENCE_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        disabledColumn: fieldSettings.disabledColumn ?? undefined,
        hiddenColumn: fieldSettings.hiddenColumn ?? undefined,
      });
    case 'TAGGED_UNION_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        tagKeyDisplayNames:
          fieldSettings.tagKeyDisplayNames == null
            ? undefined
            : objectFromEntries(
                fieldSettings.tagKeyDisplayNames.map(({value, displayName}) => [value, displayName])
              ),
      });
    default:
      throw new Error(`Found unexpected uiSchema field type ${assertNever(fieldType)} in layout`);
  }
}

function convertUISchema(uiSchema: AnalysisConfigFormField['uiSchema']): ShapeUISchemaV2 {
  return {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['field'],
    field: selectDefined({
      ...uiSchema,
      displayName: uiSchema.displayName ?? undefined,
      infoText: uiSchema.infoText ?? undefined,
      configShapePath: ['field'],
      editorOverride:
        uiSchema.editorOverride == null ? undefined : StepConfigEditorOverride[uiSchema.editorOverride],
      fieldSettings: convertFieldSettings(uiSchema.fieldSettings) ?? undefined,
    }),
  };
}

function convertConfigFormField(
  {configReferences, configSpec, uiSchema, ...configFormField}: AnalysisConfigFormField,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: AnalysisGraphQL
): ConfigFormField {
  const resolvedConfigReferences = configReferences.map((configReference) =>
    resolveConfigReference(configReference, layoutStep, analysis)
  );

  const {config, configDataFrameId, configDataFrameSpec} = resolvedConfigReferences[0];

  return {
    ...configFormField,
    config: {
      field: config,
    },
    configDataFrameId,
    configDataFrameSpec,
    configShape: convertAnalysisConfigSpec(configSpec, layoutStep, analysis),
    uiSchema: convertUISchema(uiSchema),
  };
}

function convertConfigForm(
  {configFields, ...configForm}: AnalysisConfigForm,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: AnalysisGraphQL
): ConfigFormBlock {
  return {
    ...configForm,
    configFields: configFields.map((configFormField) =>
      convertConfigFormField(configFormField, layoutStep, analysis)
    ),
  };
}

function convertGrid(
  grid: AnalysisGrid,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: AnalysisGraphQL
): Grid {
  return {
    rows: grid.rows.map((row) =>
      row.map((block) => {
        if (block.type === 'DATA_VIEW') {
          const matchingIncomingEdge = layoutStep.incomingEdges.find(
            (incomingEdge) =>
              incomingEdge.toStepData.name === block.inputReference.inputDataName &&
              incomingEdge.toStepDataIndex === block.inputReference.inputDataIndex
          )!;
          return {
            ...omit(block, 'inputReference'),
            view: getViewForViewReference(analysis, {
              stepId: matchingIncomingEdge.fromStep.id,
              outputReference: {
                stepDataName: matchingIncomingEdge.fromStepData.name,
                rawDataIndex: matchingIncomingEdge.fromStepDataIndex,
              },
            })!,
          };
        } else if (block.type === 'CONFIG_FORM') {
          return convertConfigForm(block, layoutStep, analysis);
        }

        return block;
      })
    ),
  };
}

export default function pipelineLayoutStepToDataLayout(
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: AnalysisGraphQL
): DataLayout {
  const analysisDataLayout = layoutStep.config!;
  const mappedSections = analysisDataLayout.sections.map((section) => {
    if (section.type === 'GRID') {
      return {
        ...section,
        grid: convertGrid(section.grid, layoutStep, analysis),
      };
    } else if (section.type === 'TAB') {
      return {
        ...section,
        tabs: section.tabs.map((tab) => ({...tab, grid: convertGrid(tab.grid, layoutStep, analysis)})),
      };
    } else {
      throw new Error(`Found unexpected section type ${assertNever(section).type} in layout`);
    }
  });
  return {...omit(analysisDataLayout, 'sections'), sections: mappedSections};
}
